import Message from "@/models/conversation/Message";
import { SearchOption } from "@/models/base/SearchOption";
import type { SendMessageContextFilter } from "@/services/messageService";
import type { ConversationWorkspaceDocumentSnapshot } from "@/lib/buildConversationWorkspaceDocuments";

export type StreamContext = {
  conversationId: string;
  languageCode: string;
  humanText: string;
  searchOptions: SearchOption[];
  filter: SendMessageContextFilter[];
  workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[];
  documentWorkspaceMap: Record<string, string>;
  isRetry: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringRecord = (value: unknown): value is Record<string, string> => {
  if (!isRecord(value)) return false;
  return Object.values(value).every((entry) => typeof entry === "string");
};

const isWorkspaceDocumentSnapshot = (
  value: unknown
): value is ConversationWorkspaceDocumentSnapshot => {
  if (!isRecord(value)) return false;
  return (
    typeof value.workspaceId === "string" &&
    typeof value.documentId === "string" &&
    typeof value.blobId === "string" &&
    typeof value.revisionVersion === "number"
  );
};

export const isStreamContext = (value: unknown): value is StreamContext => {
  if (!isRecord(value)) return false;
  return (
    typeof value.conversationId === "string" &&
    typeof value.languageCode === "string" &&
    typeof value.humanText === "string" &&
    Array.isArray(value.searchOptions) &&
    Array.isArray(value.filter) &&
    isStringRecord(value.documentWorkspaceMap) &&
    typeof value.isRetry === "boolean" &&
    (value.workspaceDocuments === undefined ||
      (Array.isArray(value.workspaceDocuments) &&
        value.workspaceDocuments.every(isWorkspaceDocumentSnapshot)))
  );
};

export type StreamAccumulatorState = {
  text: string;
  chainOfThoughtMessage: string;
  revisedPrompt: string;
  statusShort: string;
  statusLong: string;
  references: Message["references"];
  lemmatizedText: Record<string, string>;
};

export type InFlightStreamState = {
  conversationId: string;
  generationId: string;
  lastChunkSeq: number;
  streamHandoffDone: boolean;
  waitingForResponse: boolean;
  isTerminal: boolean;
  latestMessage: Message | null;
  streamState: StreamAccumulatorState;
  streamText: string;
  referenceReplacements: Record<string, string>;
  typingBuffer: string;
  cotBuffer: string;
  revisedPromptBuffer: string;
  statusLongBuffer: string;
  queuedCharCount: number;
  queuedCotCount: number;
  queuedRevisedPromptCount: number;
  queuedStatusLongCount: number;
  pendingClosedMessage: Message | null;
  inFlightHumanMessageId: string | null;
  inFlightHumanMessagePersisted: boolean;
  streamContext: StreamContext | null;
};

export const createEmptyStreamAccumulator = (): StreamAccumulatorState => ({
  text: "",
  chainOfThoughtMessage: "",
  revisedPrompt: "",
  statusShort: "",
  statusLong: "",
  references: [],
  lemmatizedText: {},
});

export const createInFlightStreamState = (
  conversationId: string,
  generationId: string
): InFlightStreamState => ({
  conversationId,
  generationId,
  lastChunkSeq: 0,
  streamHandoffDone: false,
  waitingForResponse: true,
  isTerminal: false,
  latestMessage: null,
  streamState: createEmptyStreamAccumulator(),
  streamText: "",
  referenceReplacements: {},
  typingBuffer: "",
  cotBuffer: "",
  revisedPromptBuffer: "",
  statusLongBuffer: "",
  queuedCharCount: 0,
  queuedCotCount: 0,
  queuedRevisedPromptCount: 0,
  queuedStatusLongCount: 0,
  pendingClosedMessage: null,
  inFlightHumanMessageId: null,
  inFlightHumanMessagePersisted: false,
  streamContext: null,
});

export const hasActiveNonTerminalStream = (
  streams: Map<string, InFlightStreamState>,
  excludeConversationId?: string
): boolean => {
  for (const [conversationId, state] of streams) {
    if (excludeConversationId && conversationId === excludeConversationId) {
      continue;
    }
    if (!state.isTerminal && state.waitingForResponse) {
      return true;
    }
  }
  return false;
};
